/* eslint-disable @typescript-eslint/no-explicit-any */
import { Injectable, OnDestroy } from '@angular/core';
import {CapacitorServicePort} from '../ports/capacitor-service.port';
import {IListener} from '../interface/listener.interface';

@Injectable({
  providedIn: 'root',
})
export class CapacitorServiceAdapter implements CapacitorServicePort, OnDestroy {
  window: any = window;

  private listeners: Record<string, IListener> = {};
  private lastListenerId = 0;

  private privateVars: any = {
    ready: false,
    platform: 'browser',
  };

  constructor() {
    this.exposeGlobalReceiveMessage();
    this.setupWebkitCheck();
  }

  private exposeGlobalReceiveMessage(): void {
    this.window.capacitorReceiveMessage = (key: string, message: any): void => {
      Object.values(this.listeners).forEach((listener) => {
        if (listener.key === key && typeof listener.handler === 'function') {
          listener.handler(message);
        }
      });
    };
  }

  private setupWebkitCheck(): void {
    if (
      this.window.webkit &&
      this.window.webkit.messageHandlers &&
      this.window.webkit.messageHandlers.cordova_iab
    ) {
      this.onWebkitAvailable();

      return;
    }

    this.set('ready', true);
  }

  private async onWebkitAvailable(): Promise<void> {
    const deviceInfo = await this.getInfo();

    this.set('deviceInfo', deviceInfo);
    this.set('platform', deviceInfo.platform);
    this.set('ready', true);
  }

  ready(): Promise<boolean> {
    return new Promise((resolve) => {
      if (this.get('ready')) {
        resolve(true);
      } else {
        setTimeout(() => {
          this.ready().then(() => {
            this.set('ready', true);
            resolve(true);
          });
        }, 100);
      }
    });
  }

  private generateRandomString(length = 10): string {
    const characters =
      'ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789';
    let result = '';

    for (let i = 0; i < length; i++) {
      result += characters.charAt(
        Math.floor(Math.random() * characters?.length)
      );
    }

    return result;
  }

  postMessage(key: string, message: unknown = {}): void {
    const webkit = this.window.webkit;

    if (
      webkit &&
      webkit.messageHandlers &&
      webkit.messageHandlers.cordova_iab
    ) {
      webkit.messageHandlers.cordova_iab.postMessage(
        JSON.stringify({ key, message })
      );
    }
  }

  addListener(
    key: string,
    handler: (message: any) => void,
    customId = ''
  ): IListener {
    this.lastListenerId++;
    const listenerId = customId || this.lastListenerId.toString();
    const listener: IListener = { id: listenerId, key, handler };

    this.listeners[listenerId] = listener;

    return listener;
  }

  removeListener(listenerOrId: string | IListener): void {
    const id =
      typeof listenerOrId === 'string' ? listenerOrId : listenerOrId?.id;

    delete this.listeners[id];
  }

  private set(key: string, value: any): void {
    this.privateVars[key] = value;
  }

  private get(key: string): any {
    return this.privateVars[key];
  }

  call(key: string, params?: object | string | number): Promise<unknown> {
    return new Promise((resolve) => {
      const listenerId = this.generateRandomString() + Date.now();

      this.addListener(
        key,
        (response) => {
          resolve(response);
          this.removeListener(listenerId);
        },
        listenerId
      );

      this.postMessage(key, params);
    });
  }

  // Predefined methods

  async getInfo(): Promise<any> {
    return await this.call('Device', `getInfo`);
  }

  async getPlatform(): Promise<string> {
    await this.ready();

    return this.get('platform') || 'browser';
  }

  async setBackgroundColor(color: string): Promise<any> {
    await this.call(
      'executeScript',
      `document.body.style.backgroundColor = "${color}";`
    );
  }

  async vibrate(serie: Array<number>): Promise<any> {
    await this.call('vibrate', serie);
  }

  async openURL(url: string): Promise<any> {
    await this.call('openURL', url);
  }

  /**
   * Cleanup all listeners when service is destroyed
   * to prevent memory leaks
   */
  ngOnDestroy(): void {
    Object.keys(this.listeners).forEach(id => {
      delete this.listeners[id];
    });

    if (this.window && this.window.capacitorReceiveMessage) {
      this.window.capacitorReceiveMessage = null;
    }

    this.privateVars = {
      ready: false,
      platform: 'browser'
    };
  }
}
